import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Image,
  Select,
  Stack,
  Text,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import Textarea from "../Textarea";
import RequiredForm from "../RequiredForm";
import FileUploader from "../FileUploader";
import formatNumber from "../../lib/formatNumber";
import {
  Clock,
  DownloadSimple,
  FloppyDisk,
  UsersThree,
} from "@phosphor-icons/react";
import formatNumberShort from "../../lib/formatNumberShort";
import formatDate from "../../lib/formatDate";
import { useFormik } from "formik";
import { Course } from "../../const/types";
import cr from "../../lib/request";
import * as yup from "yup";
import formatDuration from "../../lib/formatDuration";
import CourseOnlineStatus from "./CourseOnlineStatus";
import DeleteCourseModal from "./DeleteCourseModal";
import PublishCourseModal from "./PublishCourseModal";
import { iconSize } from "../../const/sizes";
import axios from "axios";

type Props = {
  data: Course;
};

export default function CourseEditUmumForm({ data }: Props) {
  const [mainImagePreviewUrl, setMainImagePreviewUrl] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();

  const formik = useFormik({
    validateOnChange: false,

    initialValues: {
      nama_course: data.nama_course,
      theme_id: data.theme_id,
      description: data.description,
      image: [] as File[] | null,
      materi: [] as File[] | null,
    },

    validationSchema: yup.object().shape({
      nama_course: yup.string().required("Nama Course harus diisi"),
      theme_id: yup.number().required("Tema Course harus dipilih"),
      description: yup.string().required("Deskripsi Course harus diisi"),
      image: yup
        .array()
        .test(
          "fileType",
          "Hanya file JPG, PNG, HEIC, WEBP yang diperbolehkan",
          (value) =>
            value &&
            value.every(
              (file) =>
                file.type === "image/jpeg" ||
                file.type === "image/jpg" ||
                file.type === "image/png" ||
                file.type === "image/HEIC" ||
                file.type === "image/webp"
            )
        ),
      materi: yup
        .array()
        .test(
          "fileType",
          "Hanya file ZIP, ZIPX, CBZ, RAR, 7Z, PDF, PPT, Word yang diperbolehkan",
          (value) => {
            const allowedExtensions =
              /\.(zip|zipx|cbz|rar|7z|pdf|ppt|pptx|doc|docx)$/i;
            return (
              value && value.every((file) => allowedExtensions.test(file.name))
            );
          }
        ),
    }),

    onSubmit: (values, { resetForm }) => {
      console.log(values);

      const payload = new FormData();
      payload.append("nama_course", values.nama_course);
      payload.append("theme_id", values.theme_id.toString());
      payload.append("description", values.description);
      if (values.image) {
        values.image.forEach((file) => {
          payload.append("image", file);
        });
      }
      if (values.materi) {
        values.materi.forEach((file) => {
          payload.append("materi", file);
        });
      }

      setLoading(true);
      const url = `api/course/${data.id}/updatecourse`;
      cr.post(url, payload)
        .then((r) => {
          if (r.status === 200) {
            toast({
              title: r.data.message,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        })
        .catch((error) => {
          console.log(error);
          toast({
            title: error?.response?.data?.message || "Error",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const downloadMateri = () => {
    const url = `${process.env.REACT_APP_WEB_API_BASE_URL}api/course/${data.id}/downloadmateri`;
    axios
      .get(url, {
        responseType: "blob",
        withCredentials: true,
        xsrfCookieName: "XSRF-TOKEN",
        xsrfHeaderName: "X-XSRF-TOKEN",
      })
      .then((r) => {
        if (r.status === 200) {
          const downloadUrl = window.URL.createObjectURL(new Blob([r.data]));
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute(
            "download",
            `Materi ${data.nama_course}.${r.data.type.split("/").pop()}`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
        }
      })
      .catch((e) => {
        console.log(e);
        toast({
          title: "Gagal mengunduh",
          description: e?.response?.data?.message,
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      });
  };

  return (
    <>
      <HStack
        justify={"space-between"}
        w={"100%"}
        px={5}
        py={2}
        borderBottom={"1px solid var(--divider3)"}
      >
        <CourseOnlineStatus id={data.id} />

        <HStack>
          <DeleteCourseModal data={data} />

          <PublishCourseModal course={data} />

          <Button
            size={"sm"}
            colorScheme="p"
            className="clicky"
            isLoading={loading}
            type="submit"
            form={"editCourseForm"}
            leftIcon={<Icon as={FloppyDisk} fontSize={18} weight="fill" />}
          >
            Save
          </Button>
        </HStack>
      </HStack>

      <VStack align={"stretch"} flex={1}>
        <Stack flexDir={["column", null, "row"]} gap={0} w={"100%"} flex={1}>
          <VStack
            align={"stretch"}
            gap={0}
            flex={1}
            borderRight={[null, null, "1px solid var(--divider3)"]}
            // borderBottom={["10px solid var(--divider3)", null]}
            p={5}
          >
            <form id="editCourseForm" onSubmit={formik.handleSubmit}>
              <FormControl
                mb={4}
                isInvalid={formik.errors.nama_course ? true : false}
              >
                <FormLabel>
                  Nama Course
                  <RequiredForm />
                </FormLabel>
                <Textarea
                  name="nama_course"
                  placeholder="Masukkan nama course"
                  formik={formik}
                />

                <FormErrorMessage>{formik.errors.nama_course}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.theme_id ? true : false}
              >
                <FormLabel>
                  Tema
                  <RequiredForm />
                </FormLabel>
                <Select
                  name="theme_id"
                  onChange={formik.handleChange}
                  value={formik.values.theme_id}
                >
                  {data.tema.map((t, i) => (
                    <option key={i} value={t.id}>
                      {t.theme_name}
                    </option>
                  ))}
                </Select>

                <FormErrorMessage>{formik.errors.theme_id}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.nama_course ? true : false}
              >
                <FormLabel>
                  Deskripsi
                  <RequiredForm />
                </FormLabel>
                <Textarea
                  name="description"
                  placeholder="Masukkan deskripsi"
                  formik={formik}
                  minH={"115px"}
                />

                <FormErrorMessage>{formik.errors.nama_course}</FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={formik.errors.image ? true : false}
                mb={4}
              >
                <FormLabel>Gambar Thumbnail</FormLabel>
                <FileUploader
                  name="image"
                  formik={formik}
                  setPreviewUrl={setMainImagePreviewUrl}
                  accept=".jpg, .jpeg, .png, .HEIC, .svg, .webp"
                />
                <Text mt={1} fontSize={"sm"}>
                  Optimal aspect ratio 16/9
                </Text>
                <FormErrorMessage>{formik.errors.materi}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={formik.errors.materi ? true : false}>
                <FormLabel>Materi</FormLabel>
                <FileUploader
                  name="materi"
                  formik={formik}
                  accept=".zip, .zipx, .cbz, .rar, .7z, .pdf, .doc, .docx, .pptx"
                />
                <FormErrorMessage>{formik.errors.materi}</FormErrorMessage>
              </FormControl>
            </form>
          </VStack>

          <VStack align={"stretch"} p={5}>
            <VStack
              align={"stretch"}
              borderRadius={12}
              mt={6}
              p={2}
              transition={"200ms"}
              cursor={"pointer"}
              _hover={{
                boxShadow: "0 0 0 1px var(--divider2))",
                bg: "var(--divider)",
              }}
              className="clicky"
              gap={0}
              // w={"100%"}
              w={"330px"}
              mx={"auto"}
              // border={"1px solid var(--divider3)"}
            >
              <Image
                src={
                  mainImagePreviewUrl ||
                  `${process.env.REACT_APP_MEDIA_BASE_URL}${data.image}`
                }
                objectFit={"cover"}
                aspectRatio={"16/9"}
                // mb={4}
              />

              <VStack p={2} align={"stretch"} flex={1} gap={0}>
                <Text
                  fontSize={[20, null, 22]}
                  fontWeight={700}
                  noOfLines={2}
                  mb={4}
                  lineHeight={1.4}
                >
                  {formik.values.nama_course}
                </Text>

                <HStack
                  justify={"space-between"}
                  mt={"auto"}
                  align={"flex-end"}
                  mb={4}
                >
                  <HStack opacity={0.5}>
                    <Icon as={Clock} />
                    <Text>{`${formatDuration(data.total_duration)}`}</Text>
                  </HStack>

                  <Tooltip
                    label={`${formatNumber(data.totalmembers)} mengukuti`}
                  >
                    <HStack opacity={0.5}>
                      <Icon as={UsersThree} fontSize={20} />

                      <Text noOfLines={1}>
                        {formatNumberShort(data?.totalmembers || 0)}
                      </Text>
                    </HStack>
                  </Tooltip>
                </HStack>

                <HStack align={"flex-end"} justify={"flex-end"} gap={2}>
                  <VStack gap={0} align={"flex-end"}>
                    <Text fontSize={"small"} opacity={0.5}>
                      Terakhir diperbarui
                    </Text>
                    <Text>{`${formatDate(data.updated_at)}`}</Text>
                  </VStack>
                </HStack>
              </VStack>
            </VStack>

            <Button
              className="btn-solid clicky"
              leftIcon={<Icon as={DownloadSimple} fontSize={iconSize} />}
              onClick={downloadMateri}
            >
              Tes Unduh Materi
            </Button>
          </VStack>
        </Stack>
      </VStack>
    </>
  );
}
